import {
  MessageSquare,
  Plus,
  FileText,
  Bell,
  ChevronRight,
  Users,
  Clock3,
  ClipboardCheck,
} from "lucide-react";

import { useNavigate } from "react-router-dom";
import { useEffect, useState, useRef } from "react";
import {
  UserProfileData,
  
  ITSpecialty,
} from "../../../types/admin";

import {
  Admin_GetUserDetail_LIST,
  GetMaches,
  Admin_ITSpecialty_LIST,
} from "../../../constants/adminData";

import { useAppSelector } from "../../../store";
import MessageDialog from "../../../components/MessageDialog/MessageDialog";

import {
  setPlanId,
  setFullCapabilities,
  setEmail,
  updatePersonalInfo,
  setUnreadNotificationsCount,
  setProfileIncomplete,
} from "../../../redux/slices/userProfileSlice";

import { useDispatch } from "react-redux";

import {
  getSensitiveData,
  saveSensitiveData,
} from "../../../utils/secureStorage";

import { useTheme } from "../../../context/ThemeContext";

import ApprovalStatusBadge from "../../../components/common/ApprovalStatusBadge";
import { getPendingAvailabilityCount } from "../../../services/availabilityApprovalService";
import { useMenuLabels } from '../../../hooks/useMenuLabels';

interface UserDashboardProps { }

const getNameParts = (name: string) => {
  const [firstName = "", ...lastNameParts] = name.trim().split(/\s+/).filter(Boolean);
  return { firstName, lastName: lastNameParts.join(" ") };
};

export default function UserDashboard(
  { }: Readonly<UserDashboardProps>
) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { theme } = useTheme();

  const alertShownRef = useRef(false);

  const profile = useAppSelector((state) => state.userProfile);
  const hasManagerCenterAccess = useAppSelector((state) => state.managerCenterAccess.hasAccess);

  const [userData, setUserData] =
    useState<UserProfileData | null>(null);

  
  const [specialties, setSpecialties] = useState<ITSpecialty[]>([]);

  const [loading, setLoading] = useState(true);

  const unreadNotificationsCount = profile.unreadNotificationsCount || 0;

  const [unreadMessagesCount, setUnreadMessagesCount] =
    useState(0);

  const [showIncompleteProfileDialog, setShowIncompleteProfileDialog] =
    useState(false);
  const [pendingAvailabilityCount, setPendingAvailabilityCount] = useState(0);

  const [userId, setUserId] = useState("");
  const [authToken, setAuthToken] = useState("");

  const processedMessageIdsRef = useRef<Set<string>>(new Set());
  const labels = useMenuLabels();
  const listLabel = labels.list;
  useEffect(() => {
    const loadStoredIds = async () => {
      const uid =
        profile.userId ||
        (await getSensitiveData("userId")) ||
        "";

      const token =
        profile.authToken ||
        (await getSensitiveData("authToken")) ||
        "";

      setUserId(uid);
      setAuthToken(token);
    };

    loadStoredIds();
  }, [profile]);

  useEffect(() => {
    const loadAvailabilityApprovalCount = async () => {
      try {
        const count = await getPendingAvailabilityCount();
        setPendingAvailabilityCount(count);
      } catch (error) {
        console.error("Error loading pending availability count:", error);
      }
    };

    loadAvailabilityApprovalCount();
  }, []);

  useEffect(() => {
    const loadUserProfile = async () => {
      if (!userId || !authToken) {
        return;
      }

      try {
        const getProfileSummary = async () => {
          try {
            return await Admin_GetUserDetail_LIST(userId, authToken);
          } catch (error: any) {
            if (!String(error?.code ?? '').includes('404')) {
              throw error;
            }

            // A just-restored session can occasionally reach the summary endpoint
            // before the profile lookup is ready. Confirm the 404 once before
            // treating the profile as incomplete.
            await new Promise((resolve) => globalThis.setTimeout(resolve, 750));
            return Admin_GetUserDetail_LIST(userId, authToken);
          }
        };

        const response = await getProfileSummary();

        if (response.success) {
          await saveSensitiveData(
            "userData",
            JSON.stringify(response.data)
          );

          setUserData(response.data);

          const { firstName, lastName } = getNameParts(response.data.name || "");
          dispatch(setEmail(response.data.email || profile.email));
          dispatch(updatePersonalInfo({
            firstName,
            lastName,
            profileImage: response.data.profileImage || undefined,
            photoId: response.data.photoId ?? undefined,
          }));

          dispatch(
            setPlanId(response.data.planId === 1 ? 1 : 2)
          );

          dispatch(
            setFullCapabilities(
              response.data.fullCapabilities ?? false
            )
          );

          if (
            typeof response.data.unreadNotificationsCount ===
            "number"
          ) {
            dispatch(
              setUnreadNotificationsCount(
                response.data.unreadNotificationsCount
              )
            );
          }

          if (
            typeof response.data.unreadMessageCount ===
            "number"
          ) {
            setUnreadMessagesCount(
              response.data.unreadMessageCount
            );
          }

          await saveSensitiveData(
            "incompleteProfile",
            "false"
          );

          dispatch(setProfileIncomplete(false));
        }
      } catch (error: any) {
        const statusCode = String(error?.code ?? '');

        if (statusCode.includes("404")) {
          await saveSensitiveData(
            "incompleteProfile",
            "true"
          );

          dispatch(setProfileIncomplete(true));

          if (!alertShownRef.current) {
            alertShownRef.current = true;
            setShowIncompleteProfileDialog(true);
          }

          return;
        }
      } finally {
        setLoading(false);
      }
    };

    loadUserProfile();
  }, [userId, authToken]);

  // useEffect(() => {
  //   const loadMatches = async () => {
  //     try {
  //       const response = await GetMaches();

  //       if (response.success) {
  //         setMatches(response.data.matches);

  //         const specialtiesData =
  //           await Admin_ITSpecialty_LIST();

  //         const data = Array.isArray(
  //           specialtiesData?.data
  //         )
  //           ? specialtiesData.data
  //           : [];

  //         setSpecialties(data);
  //       }
  //     } catch (error) {
  //       console.error("Error loading matches:", error);
  //     }
  //   };

  //   loadMatches();
  // }, []);

  const handleNavigateToMatches = () => {
    navigate("/findPeople");
  };

  const handleNavigateToMessages = () => {
    navigate("/OverViewMessages");
  };

  const handleNavigateToGroups = () => {
    navigate("/groups");
  };

  const handleNavigateToNotifications = () => {
    navigate("/notifications");
  };

  const handleNavigateToAvailability = () => {
    navigate("/IT-AvailabilityType", {
      state: {
        isEditMode: true,
        availabilities: userData?.availabilities || [],
      },
    });
  };

  const handleNavigateToAvailabilityApprovals = () => {
    navigate("/availability-approvals");
  };

  const formatAvailabilityDate = (value?: string | null) => {
    if (!value) return "";

    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return "";

    return date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };

const getAvailabilityDisplayItems = (availabilities?: any[]) =>
  (availabilities || []).flatMap((item, index) => {
    const typeNames = Array.isArray(item.availabilityTypeNames)
      ? item.availabilityTypeNames
      : [];

    // Multiple availability type names
    if (typeNames.length > 0) {
      return typeNames.map((name: string, typeIndex: number) => ({
        key: `${item.availabilityId ?? index}-${item.availabilityTypeIds?.[typeIndex] ?? typeIndex}`,
        label: name,
        item,
      }));
    }

    // Fallback for old/single availability response
    return [
      {
        key: item.availabilityId ?? item.availabilityTypeId ?? index,
        label:
          item.isAvailable === false
            ? 'Not Available'
            : item.availabilityTypeName || 'Available',
        item,
      },
    ];
  });

  const cardStyle = {
    backgroundColor: theme.surface,
    borderColor: theme.border,
    color: theme.text,
  };

  const mutedTextStyle = {
    color: theme.textSecondary,
  };

  const highlightCardStyle = {
    backgroundColor: theme.surface,
    borderColor: theme.border,
  };

  const roundHighlightStyle = {
    backgroundColor: theme.primary,
    color: theme.contrastText,
  };

  const actionIconCardStyle = {
    backgroundColor: theme.surfaceHover,
    color: theme.primary,
  };

  if (loading) {
    return (
      <div className="h-screen flex items-center justify-center">
        <p
          className="text-lg"
          style={mutedTextStyle}
        >
          Loading dashboard...
        </p>
      </div>
    );
  }

  if (!userData && !showIncompleteProfileDialog) {
    return (
      <div className="h-screen flex items-center justify-center">
        <p
          className="text-lg"
          style={mutedTextStyle}
        >
          Unable to load user profile
        </p>
      </div>
    );
  }

  return (
    <div
      className="min-h-screen flex flex-col items-center"
      style={{
        backgroundColor: theme.background,
      }}
    >
      <div className="w-full max-w-6xl px-4 md:px-6 lg:px-8 py-4 md:py-8 overflow-y-auto">

        {showIncompleteProfileDialog && (
          <MessageDialog
            message="Your profile is incomplete. Please complete your profile to continue."
            onOk={() => {
              setShowIncompleteProfileDialog(false);
              navigate("/personal-details");
            }}
            showCancel={false}
          />
        )}

        {!userData ? (
          <div className="h-screen flex items-center justify-center">
            <p
              className="text-lg"
              style={mutedTextStyle}
            >
              Loading profile...
            </p>
          </div>
        ) : (
          <>
            {/* Welcome */}
            <div className="mb-6 flex items-center justify-between">
              <div>
                <h1
                  className="text-lg font-semibold"
                  style={{ color: theme.text }}
                >
                  Welcome back{profile.personalInfo?.firstName ? `, ${profile.personalInfo.firstName}` : ''} 👋
                </h1>

                <p
                  className="text-sm"
                  style={mutedTextStyle}
                >
                  Here's what's happening with your
                  network today.
                </p>
              </div>

              <div className="flex items-center gap-2">
                <div className="w-2 h-2 bg-green-500 rounded-full"></div>

                <span
                  className="text-sm font-medium"
                  style={{ color: theme.text }}
                >
                  Active
                </span>
              </div>
            </div>

            {/* Availability Card */}
            <div
              className="border rounded-xl shadow-sm p-5 mb-6"
              style={cardStyle}
            >
              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">

                <div>
                  <h2
                    className="text-lg font-semibold"
                    style={{ color: theme.text }}
                  >
                    Availability Status
                  </h2>

                  <p
                    className="text-sm mt-1"
                    style={mutedTextStyle}
                  >
                    Available for up to{" "}
                    <span
                      className="font-semibold"
                      style={{ color: theme.text }}
                    >
                      {userData?.availabilities?.[0]
                        ?.availableHoursPerWeek || 0}{" "}
                      hrs/week
                    </span>
                  </p>


                  <div className="flex flex-wrap gap-2 mt-4">

                    {getAvailabilityDisplayItems(userData?.availabilities).length >
                      0 ? (
                      getAvailabilityDisplayItems(userData?.availabilities).map(
                        ({ key, label, item }: any) => (
                          <div
                            key={key}
                            className="rounded-lg border px-3 py-2"
                            style={{
                              backgroundColor:
                                theme.surfaceHover,
                              borderColor: theme.border,
                            }}
                          >
                            <div className="flex items-center gap-2">
                              <span
                                className="text-xs font-medium"
                                style={{ color: theme.primary }}
                              >
                                {label}
                              </span>
                              {/* <ApprovalStatusBadge status={item.approvalStatus} /> */}
                            </div>
                            {/* <p
                              className="mt-1 text-xs"
                              style={mutedTextStyle}
                            >
                              {item.availableHoursPerWeek || 0} Hours / Week
                            </p> */}
                            {item.submittedDate && (
                              <p
                                className="mt-1 text-xs"
                                style={mutedTextStyle}
                              >
                                Submitted: {formatAvailabilityDate(item.submittedDate)}
                              </p>
                            )}
                            {String(item.approvalStatus || "").toLowerCase() === "rejected" &&
                              item.rejectionReason && (
                              <div className="mt-2 text-xs text-red-700">
                                <p className="font-semibold">Reason:</p>
                                <p>{item.rejectionReason}</p>
                              </div>
                            )}
                          </div>
                        )
                      )
                    ) : (
                      <span
                        className="text-sm"
                        style={mutedTextStyle}
                      >
                        No availability selected
                      </span>
                    )}
                  </div>
                </div>

                <button
                  onClick={
                    handleNavigateToAvailability
                  }
                  className="px-4 py-2 rounded-lg text-sm font-semibold transition hover:opacity-90"
                  style={{
                    backgroundColor: theme.primary,
                    color: theme.contrastText,
                  }}
                >
                  Edit Availability
                </button>
              </div>
            </div>

            {hasManagerCenterAccess && (
              <button
                onClick={handleNavigateToAvailabilityApprovals}
                className="mb-6 w-full rounded-xl border p-5 text-left shadow-sm transition-all hover:-translate-y-1 hover:shadow-md duration-200"
                style={cardStyle}
              >
                <div className="flex items-center justify-between gap-4">
                  <div className="flex items-center gap-4">
                    <div
                      className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full"
                      style={roundHighlightStyle}
                    >
                      <ClipboardCheck
                        className="h-6 w-6"
                        style={{ color: theme.contrastText }}
                      />
                    </div>
                    <div>
                      <h2
                        className="text-lg font-semibold"
                        style={{ color: theme.text }}
                      >
                        {labels.availabilityApprovals}
                      </h2>
                      <p
                        className="mt-1 text-sm"
                        style={mutedTextStyle}
                      >
                        Review pending availability requests.
                      </p>
                    </div>
                  </div>
                  <span className="inline-flex min-w-7 items-center justify-center rounded-full bg-red-600 px-2 py-1 text-xs font-bold text-white">
                    {pendingAvailabilityCount}
                  </span>
                </div>
              </button>
            )}

            {/* Notifications & Groups */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">

              {/* Groups */}
              <button
                onClick={handleNavigateToGroups}
                className="border rounded-xl shadow-sm p-4 flex items-center gap-4 transition-all text-left cursor-pointer hover:-translate-y-1 hover:shadow-md duration-200"
                style={highlightCardStyle}
              >
                <div
                  className="flex-shrink-0 w-12 h-12 rounded-full flex items-center justify-center"
                  style={roundHighlightStyle}
                >
                  <Users
                    className="w-6 h-6"
                    style={{
                      color: theme.contrastText,
                    }}
                  />
                </div>

                <div className="flex-1 min-w-0">
                  <p
                    className="text-lg font-bold"
                    style={{ color: theme.text }}
                  >
                    {listLabel}
                  </p>

                  <p
                    className="text-sm mt-1"
                    style={mutedTextStyle}
                  >
                    Collaborate in project and
                    problem-solving groups.
                  </p>
                </div>

                <ChevronRight
                  className="w-5 h-5 flex-shrink-0"
                  style={mutedTextStyle}
                />
              </button>

              {/* Notifications */}
              <button
                onClick={
                  handleNavigateToNotifications
                }
                className="border rounded-xl shadow-sm p-4 flex items-center gap-4 transition-all text-left hover:-translate-y-1 hover:shadow-md duration-200"
                style={highlightCardStyle}
              >
                <div
                  className="flex-shrink-0 w-12 h-12 rounded-full flex items-center justify-center"
                  style={roundHighlightStyle}
                >
                  <Bell
                    className="w-6 h-6"
                    style={{
                      color: theme.contrastText,
                    }}
                  />
                </div>

                <div className="flex-1 min-w-0">
                  <p
                    className="text-sm"
                    style={mutedTextStyle}
                  >
                    {labels.notifications}
                  </p>

                  <p
                    className="text-2xl font-bold mt-1"
                    style={{ color: theme.text }}
                  >
                    {unreadNotificationsCount}
                  </p>
                </div>

                <ChevronRight
                  className="w-5 h-5 flex-shrink-0"
                  style={mutedTextStyle}
                />
              </button>
            </div>

            {/* Quick Actions */}
            <div className="mb-6">
              <h3
                className="text-base font-bold mb-4"
                style={{ color: theme.text }}
              >
                Quick Actions
              </h3>

              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">

                {/* Find People */}
                <button
                  onClick={
                    handleNavigateToMatches
                  }
                  className="border rounded-xl shadow-sm p-4 text-left transition hover:-translate-y-1 hover:shadow-md duration-200 min-h-[180px]"
                  style={cardStyle}
                >
                  <div className="flex items-start justify-between">
                    <div>
                      <div
                        className="w-10 h-10 rounded-lg flex items-center justify-center mb-3"
                        style={actionIconCardStyle}
                      >
                        <Users
                          className="w-5 h-5"
                          style={{
                            color: theme.primary,
                          }}
                        />
                      </div>

                      <p
                        className="font-semibold text-sm"
                        style={{
                          color: theme.text,
                        }}
                      >
                       {labels.explorePeople}
                      </p>

                      <p
                        className="text-xs mt-1"
                        style={mutedTextStyle}
                      >
                        Discover professionals by
                        skills and availability
                      </p>
                    </div>

                    <ChevronRight
                      className="w-4 h-4 flex-shrink-0 mt-1"
                      style={mutedTextStyle}
                    />
                  </div>
                </button>

                {/* Messages */}
                <button
                  onClick={
                    handleNavigateToMessages
                  }
                  className="border rounded-xl shadow-sm p-4 text-left transition hover:-translate-y-1 hover:shadow-md duration-200 min-h-[180px]"
                  style={cardStyle}
                >
                  <div className="flex items-start justify-between">
                    <div>
                      <div
                        className="w-10 h-10 rounded-lg flex items-center justify-center mb-3"
                        style={actionIconCardStyle}
                      >
                        <MessageSquare
                          className="w-5 h-5"
                          style={{
                            color: theme.primary,
                          }}
                        />
                      </div>

                      <p
                        className="font-semibold text-sm"
                        style={{
                          color: theme.text,
                        }}
                      >
                        {labels.messages}
                      </p>

                      <p
                        className="text-xs mt-1"
                        style={mutedTextStyle}
                      >
                        {unreadMessagesCount} unread
                        messages
                      </p>
                    </div>

                    <ChevronRight
                      className="w-4 h-4 flex-shrink-0 mt-1"
                      style={mutedTextStyle}
                    />
                  </div>
                </button>

                {/* Create Post */}
                <button
                  onClick={() =>
                    navigate("/CreatePost")
                  }
                  className="border rounded-xl shadow-sm p-4 text-left transition hover:-translate-y-1 hover:shadow-md duration-200 min-h-[180px]"
                  style={cardStyle}
                >
                  <div className="flex items-start justify-between">
                    <div>
                      <div
                        className="w-10 h-10 rounded-lg flex items-center justify-center mb-3"
                        style={actionIconCardStyle}
                      >
                        <Plus
                          className="w-5 h-5"
                          style={{
                            color: theme.primary,
                          }}
                        />
                      </div>

                      <p
                        className="font-semibold text-sm"
                        style={{
                          color: theme.text,
                        }}
                      >
                        {labels.createPost}
                      </p>

                      <p
                        className="text-xs mt-1"
                        style={mutedTextStyle}
                      >
                        Share updates with your
                        network
                      </p>
                    </div>

                    <ChevronRight
                      className="w-4 h-4 flex-shrink-0 mt-1"
                      style={mutedTextStyle}
                    />
                  </div>
                </button>

                {/* View Posts */}
                <button
                  onClick={() =>
                    navigate("/viewPost")
                  }
                  className="border rounded-xl shadow-sm p-4 text-left transition hover:-translate-y-1 hover:shadow-md duration-200 min-h-[180px]"
                  style={cardStyle}
                >
                  <div className="flex items-start justify-between">
                    <div>
                      <div
                        className="w-10 h-10 rounded-lg flex items-center justify-center mb-3"
                        style={actionIconCardStyle}
                      >
                        <FileText
                          className="w-5 h-5"
                          style={{
                            color: theme.primary,
                          }}
                        />
                      </div>

                      <p
                        className="font-semibold text-sm"
                        style={{
                          color: theme.text,
                        }}
                      >
                        {labels.viewPosts}
                      </p>

                      <p
                        className="text-xs mt-1"
                        style={mutedTextStyle}
                      >
                        Explore professional
                        updates
                      </p>
                    </div>

                    <ChevronRight
                      className="w-4 h-4 flex-shrink-0 mt-1"
                      style={mutedTextStyle}
                    />
                  </div>
                </button>

                {/* Availability */}
                <button
                  onClick={
                    handleNavigateToAvailability
                  }
                  className="border rounded-xl shadow-sm p-4 text-left transition hover:-translate-y-1 hover:shadow-md duration-200 min-h-[180px]"
                  style={cardStyle}
                >
                  <div className="flex items-start justify-between">
                    <div>
                      <div
                        className="w-10 h-10 rounded-lg flex items-center justify-center mb-3"
                        style={actionIconCardStyle}
                      >
                        <Clock3
                          className="w-5 h-5"
                          style={{
                            color: theme.primary,
                          }}
                        />
                      </div>

                      <p
                        className="font-semibold text-sm"
                        style={{
                          color: theme.text,
                        }}
                      >
                        My Availability
                      </p>

                      <p
                        className="text-xs mt-1"
                        style={mutedTextStyle}
                      >
                        Update availability
                        preferences
                      </p>
                    </div>

                    <ChevronRight
                      className="w-4 h-4 flex-shrink-0 mt-1"
                      style={mutedTextStyle}
                    />
                  </div>
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
